import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface the crash so it can be diagnosed (and picked up by any error tracker).
    console.error('App crash captured by ErrorBoundary:', error, info.componentStack);
    this.report(error, info.componentStack);
  }

  report(error: Error, componentStack?: string | null) {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      fetch(`${import.meta.env.VITE_API_URL || ''}/api/client-errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: error.message || 'Unknown error',
          stack: error.stack,
          componentStack,
          url: window.location.href,
          severity: 'error',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* never let reporting throw */ }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 p-6 text-center dark:bg-gray-900">
        <div className="text-4xl">😕</div>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
          Algo ha fallado al cargar esta sección
        </h1>
        <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
          Hemos registrado el error. Prueba a recargar la página; si vuelve a ocurrir, contacta con soporte.
        </p>
        <button
          onClick={this.handleReload}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Recargar
        </button>
      </div>
    );
  }
}
